import { useCallback, useEffect, useState } from "react";
import { type PreferenceKey, api } from "./api";
import { readStore, writeStore } from "./storage";

/**
 * The presenter's settings live in the database. This browser keeps a copy only so the first paint is right (no
 * light flash before a dark presenter) and so the notes window follows a theme change at once.
 */
const LOCAL: Record<PreferenceKey, string> = {
  theme: "hali-shares:theme",
  "studio-thumbs": "hali-shares:studio-thumbs",
  "preview-open": "hali-shares:preview-open",
};

let server: Partial<Record<PreferenceKey, unknown>> | null = null;
// Set on this page since it loaded: newer than whatever the first read returns, so that read mustn't undo them.
const written: Partial<Record<PreferenceKey, unknown>> = {};
let loading: Promise<void> | null = null;
const listeners = new Set<() => void>();

/** Loads once per page. A value only this browser has (from before preferences were stored) is copied up. */
function load(): Promise<void> {
  loading ??= api.preferences().then(
    async (stored) => {
      server = { ...stored, ...written };
      for (const key of Object.keys(LOCAL) as PreferenceKey[]) {
        const local = readStore<unknown>(LOCAL[key], undefined);
        if (server[key] === undefined && local !== undefined) {
          server[key] = local;
          await api.setPreference(key, local).catch(() => {});
        }
      }
      listeners.forEach((f) => f());
    },
    () => {
      // Signed out or offline: keep the local copy, and try again next time a page asks (e.g. after sign-in).
      loading = null;
    },
  );
  return loading;
}

export function usePreference<T>(key: PreferenceKey, fallback: T): [T, (v: T) => void] {
  const [value, setValue] = useState<T>(() => (server?.[key] as T) ?? readStore<T>(LOCAL[key], fallback));
  useEffect(() => {
    const sync = () => {
      const v = server?.[key];
      if (v === undefined) return;
      setValue(v as T);
      writeStore(LOCAL[key], v);
    };
    // Another window of this browser changed it (presenter ↔ notes window): follow at once.
    const onStorage = (e: StorageEvent) => {
      if (e.key === LOCAL[key]) setValue(readStore<T>(LOCAL[key], fallback));
    };
    listeners.add(sync);
    window.addEventListener("storage", onStorage);
    void load().then(sync);
    return () => {
      listeners.delete(sync);
      window.removeEventListener("storage", onStorage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fallback is a default, not an input
  }, [key]);
  const set = useCallback(
    (v: T) => {
      setValue(v);
      writeStore(LOCAL[key], v);
      written[key] = v;
      if (server) server[key] = v;
      api.setPreference(key, v).catch(() => {});
    },
    [key],
  );
  return [value, set];
}
